import type { Statement, Policy } from './types.js'

/**
 * Resolves a selector string against a data object, inspired by jq.
 *
 * @returns The selected value, or null if the selection fails at an optional part.
 * @throws Error if the selection fails at a non-optional part.
 */
function resolveSelector (data:unknown, selector:string):unknown {
    if (selector === '.') {
        return data
    }

    // This regex is a simplified parser for the selector syntax.
    const partsRegex = /(\.[\w_]+|\[-?\d*:-?\d*\]|\[-?\d+\]|\[\]|\?)/g
    const parts = selector.match(partsRegex) || []

    let currentData = data

    for (let part of parts) {
        let isOptional = false
        if (part.endsWith('?')) {
            isOptional = true
            part = part.slice(0, -1)
        }

        try {
            if (currentData === undefined || currentData === null) {
                throw new Error(
          `Cannot process part "${part}" on null or undefined value.`
                )
            }

            if (part.startsWith('.')) {
                const key = part.substring(1)
                if (typeof currentData !== 'object' || Array.isArray(currentData)) {
                    throw new Error(`Cannot access key "${key}" on non-object.`)
                }
                currentData = (currentData as Record<string, unknown>)[key]
            } else if (part === '[]') {
                if (Array.isArray(currentData)) {
                    // No-op for arrays, the quantifier will iterate over it.
                } else if (typeof currentData === 'object') {
                    currentData = Object.values(currentData as Record<string, unknown>)
                } else {
                    throw new Error('Cannot extract values from non-collection.')
                }
            } else if (part.startsWith('[') && part.includes(':')) {
                // Slice
                if (!Array.isArray(currentData)) {
                    throw new Error('Slice can only be applied to arrays.')
                }
                const [start, end] = part
                    .substring(1, part.length - 1)
                    .split(':')
                    .map((s) => (s ? Number.parseInt(s, 10) : undefined))
                currentData = currentData.slice(start, end)
            } else if (part.startsWith('[')) {
                // Index
                if (!Array.isArray(currentData)) {
                    throw new Error('Index can only be applied to arrays.')
                }
                const index = Number.parseInt(part.substring(1, part.length - 1), 10)
                currentData =
          currentData[index < 0 ? currentData.length + index : index]
            }

            if (currentData === undefined) {
                throw new Error(`Path segment returned undefined: ${part}`)
            }
        } catch (e) {
            if (isOptional) {
                return null
            }
            // Re-throw if not optional, to be caught by evaluateStatement
            throw e
        }
    }

    return currentData
}

/**
 * Performs a deep equality check between two values.
 */
function deepEqual (a:unknown, b:unknown):boolean {
    if (a === b) return true
    if (a && b && typeof a === 'object' && typeof b === 'object') {
        if (a.constructor !== b.constructor) return false
        if (Array.isArray(a)) {
            if (a.length !== (b as unknown[]).length) return false
            for (let i = 0; i < a.length; i++) {
                if (!deepEqual(a[i], (b as unknown[])[i])) return false
            }
            return true
        }
        const keys = Object.keys(a)
        if (keys.length !== Object.keys(b).length) return false

        for (const key of keys) {
            const isEq = ((b as Record<string, unknown>)
                .hasOwnProperty(key) ||  // eslint-disable-line
                !deepEqual(
                    (a as Record<string, unknown>)[key],
                    (b as Record<string, unknown>)[key]
                ))

            if (!isEq) {
                return false
            }
        }
        return true
    }
    return false
}

/**
 * Converts a UCAN glob pattern to a regular expression.
 */
function likeRegex (pattern:string):RegExp {
    // Convert UCAN glob pattern to a regular expression.
    // The glob pattern has `*` as a wildcard (zero or more chars)
    // and `\*` as an escape for a literal asterisk.
    let regexString = '^'
    let i = 0
    while (i < pattern.length) {
        const char = pattern[i]

        if (char === '\\') {
            // Handle escape sequence
            if (i + 1 < pattern.length) {
                const nextChar = pattern[i + 1]
                if (nextChar === '*') {
                    // This is an escaped asterisk `\*`. Match a literal `*`.
                    regexString += '\\*'
                    i += 2 // Move past both `\` and `*`
                } else {
                    // This is another escape, like `\\`. Treat the `\` as a literal character.
                    regexString += '\\\\'
                    i++
                }
            } else {
                // Trailing backslash. Treat as a literal `\`.
                regexString += '\\\\'
                i++
            }
        } else if (char === '*') {
            // This is a wildcard `*`. Match zero or more characters.
            regexString += '.*'
            i++
        } else {
            // This is a regular character. Escape it if it's a special regex metacharacter.
            regexString += char.replace(/[.+?^${}()|[\]\\]/g, '\\$&')
            i++
        }
    }
    regexString += '$'

    return new RegExp(regexString)
}

/**
 * Evaluates a single policy statement against the invocation args.
 *
 * @returns The result of the evaluation.
 */
function evaluateStatement<Args> (args:Args, statement:Statement<Args>):boolean {
    const [op, ...params] = statement

    try {
        switch (op) {
            // Comparisons
            case '==': {
                const [selector, value] = params as [string, unknown]

                return deepEqual(resolveSelector(args, selector), value)
            }
            case '!=': {
                const [selector, value] = params as [string, unknown]
                return !deepEqual(resolveSelector(args, selector), value)
            }
            case '>':
            case '>=':
            case '<':
            case '<=': {
                const [selector, value] = params as [string, number]
                const selected = resolveSelector(args, selector)
                if (typeof selected !== 'number' || typeof value !== 'number') { return false }
                if (op === '>') return selected > value
                if (op === '>=') return selected >= value
                if (op === '<') return selected < value
                if (op === '<=') return selected <= value
                break
            }
            // Glob Matching
            case 'like': {
                const [selector, pattern] = params as [string, string]
                const selected = resolveSelector(args, selector)
                if (typeof selected !== 'string') return false
                const regex = likeRegex(pattern)
                return regex.test(selected)
            }
            // Connectives
            case 'and': {
                const [statements] = params as [Statement<Args>[]]
                return statements.every((stmt) => evaluateStatement(args, stmt))
            }
            case 'or': {
                const [statements] = params as [Statement<Args>[]]
                if (statements.length === 0) return true
                return statements.some((stmt) => evaluateStatement(args, stmt))
            }
            case 'not': {
                const [statement] = params as [Statement<Args>]
                return !evaluateStatement(args, statement)
            }
            // Quantification
            case 'all':
            case 'any': {
                const [selector, innerStmt] =
                params as [string, Statement<unknown>]
                let collection = resolveSelector(args, selector)

                if ((innerStmt as Statement<unknown>)[0] === 'any' || (innerStmt as Statement<unknown>)[0] === 'all') {
                    return evaluateStatement(collection, innerStmt)
                }

                if (
                    collection &&
          typeof collection === 'object' &&
          !Array.isArray(collection)
                ) {
                    collection = Object.values(collection as Record<string, unknown>)
                }
                if (!Array.isArray(collection)) return false
                if (collection.length === 0) return true
                if (op === 'all') {
                    return collection.every((item) => {
                        return evaluateStatement(item, innerStmt)
                    })
                }
                // any
                return collection.some((item) => evaluateStatement(item, innerStmt))
            }
            default:
                return false // Unknown operator
        }
    } catch {
    // As per the spec, if a selector cannot be resolved, the statement MUST return false.
        return false
    }
    return false
}

/**
 * Validates invocation arguments against a UCAN policy.
 *
 * @returns True if the args are valid according to the policy, false otherwise.
 */
export function validate<Args> (args:Args, policy:Policy<Args>):boolean {
    // The top-level policy is an implicit 'and'.
    return policy.every((statement) => evaluateStatement(args, statement))
}
